import React, { FC, PropsWithChildren } from "react";

import { useTranslation } from "react-i18next";
import IconButton from "@mui/material/IconButton";

import { EditPencil } from "@/assets/icons";
import { isABroker } from "@/shared/hooks";
import { StyledPendingChip } from "@/styles/styled";

import { StyledActionGroup, StyledFormPanelHeader, StyledInfosContainer, StyledTitle } from "../../styled";
import { FormPanelInfo, mapGroupComponents } from "../../utils";
import InfoFieldRead from "../InfoFieldRead/InfoFieldRead";

type ProfileFormPanelProps = {
    title?: string;
    stringDivisor?: string;
    isEditable?: boolean;
    panelInfo: FormPanelInfo;
    readCustomSize?: boolean;
    onClickEdit?: () => void;
};

export const ProfileFormPanel: FC<PropsWithChildren<ProfileFormPanelProps>> = ({
    title,
    children,
    panelInfo,
    stringDivisor,
    onClickEdit,
    readCustomSize = false,
    isEditable = false,
}) => {
    const { data, pending = false, groups } = panelInfo;
    const { t } = useTranslation();
    let groupComponents = {};

    if (Object.keys(groups).length !== 0) {
        groupComponents = mapGroupComponents(data, groups);
    }

    const renderGroupField = (group) =>
        group
            .filter((field, i) => field.some((insideField) => !!insideField.readValue) || !i)
            .map((field) => {
                const readValues = field
                    .filter((insideField) => !!insideField.readValue)
                    .map((insideField) => insideField.readValue)
                    .join(stringDivisor || " ");

                return (
                    <InfoFieldRead
                        key={`field-${field[0].id}`}
                        label={t(field[0].label)}
                        readCustomSize={readCustomSize}
                        value={readValues || "-"}
                    />
                );
            });

    const renderGroupInfo = () => {
        const renderComponents = [];
        Object.keys(groupComponents).forEach((key) => {
            renderComponents.push(renderGroupField(groupComponents[key]));
        });
        return renderComponents;
    };

    const renderInfo = () => {
        return data
            .filter((field) => !field.group)
            .filter((field) => !field.hideRead)
            .map((field) => (
                <InfoFieldRead
                    key={`field-${field.id}`}
                    label={t(field.label)}
                    readCustomSize={readCustomSize}
                    value={field.readValue || "-"}
                    size={field.size}
                />
            ));
    };

    return (
        <>
            <StyledFormPanelHeader>
                <StyledTitle>{title}</StyledTitle>
                <StyledActionGroup>
                    {isEditable && pending && <StyledPendingChip>{t("Common.Pending")}</StyledPendingChip>}
                    {!isABroker() && isEditable && (
                        <IconButton
                            data-testid="form_panel-button-edit"
                            onClick={onClickEdit}
                            aria-label={t("Common.Edit")}
                        >
                            <EditPencil />
                        </IconButton>
                    )}
                </StyledActionGroup>
            </StyledFormPanelHeader>
            {children}
            <StyledInfosContainer>
                {renderGroupInfo()}
                {renderInfo()}
            </StyledInfosContainer>
        </>
    );
};

export default ProfileFormPanel;
